import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Send, MessageCircle, Maximize2, Minimize2, Mic, MicOff, Video, VideoOff, ScreenShare, PhoneOff } from 'lucide-react';
import { useSupabaseAuthStore } from '../stores/supabaseAuthStore';
import { databaseService } from '../services/databaseService';
import { supabaseChatService, ChatMessage } from '../services/supabaseChatService';
import { supabase } from '../config/supabase';
import { livekitConfig } from '../config/livekit';
import GiftBurst from './GiftBurst';
import {
  LiveKitRoom as LiveKitRoomOriginal,
  useRoomContext,
  useTracks,
  ParticipantTile,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';

/** LiveKitRoom is typed as ReactNode; cast to any so strict JSX (Element | null) accepts it */
const LiveKitRoom = LiveKitRoomOriginal as any;

interface Props {
  roomName: string;
  isStreamer?: boolean;
}

/** Wrapper so ParticipantTile (typed as ReactNode) is valid JSX (Element | null) */
function TrackTile({ trackRef }: { trackRef: TrackReferenceOrPlaceholder }): React.ReactElement {
  return React.createElement(
    ParticipantTile as React.ComponentType<{ trackRef: TrackReferenceOrPlaceholder }>,
    { trackRef }
  );
}

// Inner content rendered inside LiveKitRoom (has room context)
function LiveStreamVideoContent({
  isStreamer,
  streamData,
  formatViewerCount,
  showChat,
  setShowChat,
  onHangup,
  viewersCount,
  setLiveKitReady,
}: {
  isStreamer: boolean;
  streamData: { title: string; hostName: string; hostAvatar: string; viewerCount: number };
  formatViewerCount: (n: number) => string;
  showChat: boolean;
  setShowChat: (v: boolean) => void;
  onHangup: () => void;
  viewersCount: number;
  setLiveKitReady: (v: boolean) => void;
}) {
  const room = useRoomContext();
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  useEffect(() => {
    setLiveKitReady(true);
  }, [setLiveKitReady]);

  const handleToggleMic = async () => {
    const enabled = room.localParticipant.isMicrophoneEnabled;
    await room.localParticipant.setMicrophoneEnabled(!enabled);
  };

  const handleToggleCamera = async () => {
    const enabled = room.localParticipant.isCameraEnabled;
    await room.localParticipant.setCameraEnabled(!enabled);
  };

  const handleToggleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        await room.localParticipant.setScreenShareEnabled(false);
        setIsScreenSharing(false);
      } else {
        await room.localParticipant.setScreenShareEnabled(true);
        setIsScreenSharing(true);
      }
    } catch (e) {
      console.error('Screen share error:', e);
    }
  };

  const handleDisconnect = () => {
    room.disconnect();
    onHangup();
  };

  const displayViewerCount = viewersCount > 0 ? viewersCount : streamData.viewerCount;

  return (
    <>
      <div className="w-full h-full flex items-center justify-center bg-black">
        {tracks.length === 0 ? (
          <div className="text-gray-400">Waiting for participants...</div>
        ) : (
          <div className="grid gap-2 p-2 w-full h-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 content-center justify-items-center">
            {tracks.map((trackRef) => (
              <TrackTile key={`${trackRef.participant.identity}-${trackRef.source}`} trackRef={trackRef} />
            ))}
          </div>
        )}
      </div>

      {/* Stream Info Overlay - same as before */}
      <div className="absolute left-0 right-0 bottom-0 z-10">
        <div className="bg-amber-950 p-4 border-t border-amber-800 pointer-events-auto shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center overflow-hidden">
                {streamData.hostAvatar ? (
                  <img
                    src={streamData.hostAvatar}
                    alt={streamData.hostName}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('span')) {
                        const fallback = document.createElement('span');
                        fallback.className = 'text-white font-semibold';
                        fallback.textContent = streamData.hostName.charAt(0);
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                ) : (
                  <span className="text-white font-semibold">{streamData.hostName.charAt(0)}</span>
                )}
              </div>
              <div>
                <h1 className="text-white text-xl font-bold">{streamData.title}</h1>
                <span className="text-gray-300 text-sm">{formatViewerCount(displayViewerCount)} watching</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {isStreamer && (
                <>
                  <button
                    onClick={handleToggleMic}
                    title={room.localParticipant.isMicrophoneEnabled ? 'Mute' : 'Unmute'}
                    className="p-3 bg-black bg-opacity-50 text-white rounded-xl hover:bg-opacity-70 transition-all"
                  >
                    {room.localParticipant.isMicrophoneEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
                  </button>
                  <button
                    onClick={handleToggleCamera}
                    title={room.localParticipant.isCameraEnabled ? 'Stop camera' : 'Start camera'}
                    className="p-3 bg-black bg-opacity-50 text-white rounded-xl hover:bg-opacity-70 transition-all"
                  >
                    {room.localParticipant.isCameraEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                  </button>
                  <button
                    onClick={handleToggleScreenShare}
                    title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                    className="p-3 bg-black bg-opacity-50 text-white rounded-xl hover:bg-opacity-70 transition-all"
                  >
                    <ScreenShare className="w-6 h-6" />
                  </button>
                </>
              )}
              <button
                onClick={() => setShowChat(!showChat)}
                title={showChat ? 'Hide chat' : 'Show chat'}
                className="p-3 bg-black bg-opacity-50 text-white rounded-xl hover:bg-opacity-70 transition-all"
              >
                <MessageCircle className="w-6 h-6" />
              </button>
              {isStreamer && (
                <button
                  onClick={handleDisconnect}
                  title="End stream"
                  className="p-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                >
                  <PhoneOff className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const LiveStream: React.FC<Props> = ({ roomName: propRoomName, isStreamer = false }) => {
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const rawRoomName = urlParams.get('room') || propRoomName || 'bible-study';
  const roomName = rawRoomName.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  const { user } = useSupabaseAuthStore();

  const [showChat, setShowChat] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [liveKitReady, setLiveKitReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [, setModerators] = useState<string[]>([]);
  const [viewers, setViewers] = useState<any[]>([]);
  const [, setIsModerator] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [streamData, setStreamData] = useState<{
    title: string;
    hostName: string;
    hostAvatar: string;
    viewerCount: number;
    hostId?: string;
  }>({
    title: 'Live Stream',
    hostName: 'BibleNOW User',
    hostAvatar: '',
    viewerCount: 0,
    hostId: undefined,
  });
  const [giftOverlay, setGiftOverlay] = useState<null | { amount: number; sender?: string }>(null);
  const [isEnding, setIsEnding] = useState(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleStreamEnd = useCallback(async () => {
    if (isEnding) return;
    setIsEnding(true);
    try {
      if (user) {
        await databaseService.endStreamOnRedirect(user.uid);
      }
    } catch (err) {
      console.error('Error ending stream:', err);
    } finally {
      window.location.href = '/endstream';
    }
  }, [user, isEnding]);

  const trackViewerJoin = useCallback(
    async (userId: string, livestreamId?: string) => {
      if (!user) return;
      try {
        const { supabase: sb } = await import('../config/supabase');
        let streamId = livestreamId;
        if (!streamId) {
          const roomParam = new URLSearchParams(window.location.search).get('room');
          if (roomParam) {
            const { data: streamDataRow } = await sb
              .from('livestreams')
              .select('id')
              .eq('room_name', roomParam)
              .eq('is_live', true)
              .single();
            if (streamDataRow) streamId = streamDataRow.id;
          }
        }
        if (streamId) {
          await sb
            .from('livestream_viewers')
            .upsert(
              {
                livestream_id: streamId,
                user_id: userId,
                joined_at: new Date().toISOString(),
                is_active: true,
                left_at: null,
              },
              { onConflict: 'livestream_id,user_id' }
            );
        }
      } catch (err) {
        console.error('Error in trackViewerJoin:', err);
      }
    },
    [user]
  );

  const trackViewerLeave = useCallback(
    async (userId: string) => {
      if (!user) return;
      try {
        const { supabase: sb } = await import('../config/supabase');
        const roomParam = new URLSearchParams(window.location.search).get('room');
        let streamId: string | undefined;
        if (roomParam) {
          const { data: streamDataRow } = await sb
            .from('livestreams')
            .select('id')
            .eq('room_name', roomParam)
            .eq('is_live', true)
            .single();
          if (streamDataRow) streamId = streamDataRow.id;
        }
        if (streamId) {
          await sb
            .from('livestream_viewers')
            .update({ left_at: new Date().toISOString(), is_active: false })
            .eq('livestream_id', streamId)
            .eq('user_id', userId);
        }
      } catch (err) {
        console.error('Error in trackViewerLeave:', err);
      }
    },
    [user]
  );

  const fetchViewers = useCallback(async () => {
    try {
      const { supabase: sb } = await import('../config/supabase');
      const roomParam = new URLSearchParams(window.location.search).get('room');
      if (!roomParam) return;
      const { data: streamDataRow, error: streamError } = await sb
        .from('livestreams')
        .select('id')
        .eq('room_name', roomParam)
        .eq('is_live', true)
        .single();
      if (streamError || !streamDataRow) return;
      const { data: viewersData, error: viewersError } = await sb
        .from('livestream_viewers')
        .select('*')
        .eq('livestream_id', streamDataRow.id)
        .eq('is_active', true)
        .order('joined_at', { ascending: true });
      if (!viewersError && viewersData) {
        setViewers(viewersData);
      }
    } catch (err) {
      console.error('Error in fetchViewers:', err);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (user?.uid) trackViewerLeave(user.uid);
    };
  }, [user?.uid, trackViewerLeave]);

  useEffect(() => {
    if (liveKitReady) {
      fetchViewers();
      const interval = setInterval(fetchViewers, 10000);
      return () => clearInterval(interval);
    }
  }, [liveKitReady, fetchViewers]);

  useEffect(() => {
    setStreamData((prev) => ({
      ...prev,
      viewerCount: viewers.length,
    }));
  }, [viewers.length]);

  useEffect(() => {
    const fetchStreamData = async () => {
      try {
        const titleParam = urlParams.get('title');
        setStreamData((prev) => ({
          ...prev,
          title: titleParam ? decodeURIComponent(titleParam) : roomName.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        }));
        if (user?.uid) {
          try {
            const userProfile = await databaseService.getUserProfile(user.uid);
            if (userProfile) {
              setStreamData((prev) => ({
                ...prev,
                hostName: userProfile.full_name || userProfile.username || user?.displayName || 'BibleNOW User',
                hostAvatar: userProfile.profile_photo_url || userProfile.avatar_url || '',
                hostId: user.uid,
              }));
            } else {
              setStreamData((prev) => ({ ...prev, hostName: user?.displayName || 'BibleNOW User', hostId: user.uid }));
            }
          } catch {
            setStreamData((prev) => ({ ...prev, hostName: user?.displayName || 'BibleNOW User', hostId: user.uid }));
          }
        }
      } catch (err) {
        console.error('Error fetching stream data:', err);
      }
    };
    fetchStreamData();
  }, [roomName, user?.uid, user?.displayName, urlParams]);

  useEffect(() => {
    const handleMessages = (newMessages: ChatMessage[]) => setMessages(newMessages);
    supabaseChatService.subscribeToMessages(roomName, handleMessages);
    return () => supabaseChatService.unsubscribeFromMessages();
  }, [roomName]);

  useEffect(() => {
    if (!roomName) return;
    const channel = supabase
      .channel(`gifts:${roomName}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'shekel_gifts', filter: `context_id=eq.${roomName}` },
        async (payload: any) => {
          const gift = payload.new as any;
          setGiftOverlay({ amount: gift.amount, sender: gift.sender_name });
          try {
            await supabase.from('livestream_chat').insert([
              {
                room_id: roomName,
                user_id: gift.sender_id,
                user_name: 'System',
                text: `🎁 ${gift.sender_name || 'A viewer'} sent ${gift.amount} Shekelz!`,
                is_moderator: true,
              },
            ]);
          } catch {}
        }
      )
      .subscribe();
    return () => {
      channel.unsubscribe();
    };
  }, [roomName]);

  // Fetch LiveKit token when user and room are ready
  useEffect(() => {
    if (!user || !roomName || !livekitConfig.serverUrl) return;
    let cancelled = false;
    (async () => {
      setTokenError(null);
      try {
        const res = await fetch('/api/livekit/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            room: roomName,
            userId: user.uid,
            name: user.displayName || 'BibleNOW User',
            email: user.email,
            isStreamer,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || res.statusText);
        }
        const data = await res.json();
        if (!cancelled) setToken(data.token);
      } catch (err) {
        if (!cancelled) {
          setTokenError(err instanceof Error ? err.message : 'Failed to get token');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, roomName, isStreamer]);

  const formatViewerCount = (count: number) => {
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      await supabaseChatService.sendMessage(roomName, newMessage, false, streamData.hostAvatar);
      setNewMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex h-screen bg-gray-900 items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">BibleNOW Studio</h1>
          <p className="mb-4">Please log in to access the livestream.</p>
          <p>Redirecting to app download...</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="flex h-screen bg-gray-900 items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Connection error</h1>
          <p className="mb-4">{tokenError}</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex h-screen bg-gray-900 items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Connecting...</h1>
          <p>Getting ready to join the livestream.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-900">
      <div className="relative flex-1 bg-black">
        <LiveKitRoom
          serverUrl={livekitConfig.serverUrl}
          token={token}
          connect={true}
          audio={isStreamer}
          video={isStreamer}
          onConnected={() => {
            if (user?.uid) {
              trackViewerJoin(user.uid);
              fetchViewers();
            }
            if (isStreamer && user?.uid) {
              setModerators((prev) => (prev.includes(user.uid) ? prev : [...prev, user.uid]));
              setIsModerator(true);
            }
          }}
          onDisconnected={() => {
            if (user?.uid) trackViewerLeave(user.uid);
            handleStreamEnd();
          }}
          onError={(err: Error) => setError(err.message)}
          style={{ height: '100%', width: '100%' }}
        >
          <LiveStreamVideoContent
            isStreamer={!!isStreamer}
            streamData={streamData}
            formatViewerCount={formatViewerCount}
            showChat={showChat}
            setShowChat={setShowChat}
            onHangup={handleStreamEnd}
            viewersCount={viewers.length}
            setLiveKitReady={setLiveKitReady}
          />
        </LiveKitRoom>

        {giftOverlay && (
          <GiftBurst amount={giftOverlay.amount} senderName={giftOverlay.sender} onDone={() => setGiftOverlay(null)} />
        )}

        <div className="absolute top-4 right-4 flex space-x-2">
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-70 transition-all"
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {showChat && (
        <div className="w-80 border-l border-gray-700 flex flex-col h-full">
          <div className="p-4 border-b border-yellow-500 bg-gray-800">
            <h3 className="text-white font-semibold">Live Chat</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${message.userId === user?.uid ? 'flex-row-reverse space-x-reverse' : ''}`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center overflow-hidden">
                      {message.userAvatar ? (
                        <img
                          src={message.userAvatar}
                          alt={message.userName}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent && !parent.querySelector('span')) {
                              const fallback = document.createElement('span');
                              fallback.className = 'text-white text-sm font-semibold';
                              fallback.textContent = message.userName.charAt(0);
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <span className="text-white text-sm font-semibold">{message.userName.charAt(0)}</span>
                      )}
                    </div>
                  </div>
                  <div className={`flex-1 max-w-xs ${message.userId === user?.uid ? 'text-right' : ''}`}>
                    <div
                      className={`inline-block px-3 py-2 rounded-lg ${
                        message.userId === user?.uid ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`text-xs font-medium ${message.userId === user?.uid ? 'text-black' : 'text-gray-300'}`}>
                          {message.userName}
                        </span>
                      </div>
                      <p className="text-sm break-words">{message.text}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          {error && (
            <div className="px-4 py-2 bg-red-900 text-red-200 text-sm">{error}</div>
          )}
          <div className="p-4 border-t border-gray-700">
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !newMessage.trim()}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStream;
